<script setup lang="ts">
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { ref } from 'vue'
import { useDeviceType } from '@/composables/useDeviceType'
import UiMenuDialog from '@/components/ui/menu/dialog/index.vue'

export type Direction =
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right'
    | 'top-left'
    | 'top-right'

defineProps<{
    title?: string
    description?: string
    items?: MenuItem[]
    direction?: Direction
    simple?: boolean
    primary?: boolean
}>()

const { isDesktop, isTouchScreen } = useDeviceType()

const isOpen = ref(false)

async function callItemAction(event: Event, item: MenuItem) {
    if (item.action) {
        await item.action(event)
    }

    isOpen.value = false
}
</script>

<template>
    <UiMenuDialog
        v-if="!isDesktop || isTouchScreen"
        :title="title"
        :description="description"
        :items="items"
    >
        <template #trigger="{ toggle }">
            <slot name="trigger" :click="toggle" />
        </template>

        <template #trigger-content>
            <slot name="trigger-content">
                {{ title }}
            </slot>
        </template>
    </UiMenuDialog>

    <Menu v-else as="div" class="relative inline-block text-left">
        <MenuButton
            :as="$slots.trigger ? 'template' : undefined"
            class="align-middle"
        >
            <slot name="trigger" :click="() => null">
                <slot name="trigger-content">
                    {{ title }}
                </slot>
            </slot>
        </MenuButton>

        <Transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <MenuItems
                as="div"
                class="absolute z-[1000] max-h-60 min-w-full overflow-auto truncate rounded bg-white text-base focus:outline-none sm:text-sm"
                :class="[
                    simple ? '' : 'border border-black',
                    direction === 'bottom'
                        ? 'left-1/2 mt-1 -translate-x-1/2 transform'
                        : direction === 'top'
                        ? 'bottom-full left-1/2 mb-1 -translate-x-1/2 transform'
                        : direction === 'right'
                        ? 'right-0 top-0 ml-1'
                        : direction === 'bottom-left'
                        ? 'left-0 mt-1'
                        : direction === 'bottom-right'
                        ? 'right-0 mt-1'
                        : direction === 'top-left'
                        ? 'bottom-full left-0 mb-1'
                        : direction === 'top-right'
                        ? 'bottom-full right-0 mb-1'
                        : 'left-0 top-0 ml-1'
                ]"
            >
                <div
                    :class="[
                        'py-1',
                        primary ? 'bg-sun-yellow/50' : 'bg-neutral'
                    ]"
                >
                    <slot>
                        <MenuItem
                            v-for="(item, index) in items"
                            v-slot="{ active }"
                            :key="index"
                        >
                            <button
                                type="button"
                                :class="[
                                    active
                                        ? primary
                                            ? 'bg-sun-yellow text-white'
                                            : 'bg-gray'
                                        : 'text-black',
                                    'flex w-full items-center px-2 py-2 text-sm'
                                ]"
                                :title="item.label"
                                @click="callItemAction($event, item)"
                            >
                                {{ item.label }}
                            </button>
                        </MenuItem>
                    </slot>
                </div>
            </MenuItems>
        </Transition>
    </Menu>
</template>